import React, { useMemo, useState } from 'react';

import { formatStockQuantity } from '../utils/stockDisplay';
import { getNextSortConfig, sortRows } from '../utils/tableSort';
import { calculatePackPricingFromRow } from '../utils/packPricing';
import { downloadCsv } from '../utils/bulkCsv';
import { buildStockDispatchRows, DISPATCH_STOCK_HEADERS } from '../utils/stockDispatchExport';
import { buildStockMarginRows, MARGIN_HEADERS } from '../utils/bulkMargins';
import { createCatalogTeluguResolver, getProductTeluguName } from '../utils/catalogTeluguName';
import InventoryLabelExport from './InventoryLabelExport';
import InventoryStockLabelPrint from './InventoryStockLabelPrint';
import { smallerPackCosts } from '../utils/stockPackCosts';

const firstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== '');

const getInventoryBrandName = (item) =>
  firstValue(
    item?.brand_name_english,
    item?.brand_name,
    item?.brand,
    item?.brandName,
    item?.brandNameEnglish
  );

const getInventoryCategoryName = (item) =>
  firstValue(item?.category_name_english, item?.category_name, item?.categoryName, item?.category_name_telugu);

const getInventoryProductName = (item) =>
  firstValue(
    item?.product_name,
    item?.product_name_eng,
    item?.product_name_tel,
    item?.productName,
    item?.name,
    item?.product_id
  );

const getInventoryProductDisplayName = (item) => {
  const brand = String(getInventoryBrandName(item) || '').trim();
  const product = String(getInventoryProductName(item) || '').trim();

  if (!brand) return product || '-';
  if (!product) return brand;
  if (product.toLowerCase().startsWith(brand.toLowerCase())) return product;

  return `${brand} ${product}`;
};

const getInventoryStockQuantity = (item) =>
  firstValue(
    item?.count_in_stock,
    item?.countInStock,
    item?.stock,
    item?.qty,
    item?.quantityInStock,
    item?.no_of_units
  );

const getInventoryStockDisplay = (item) =>
  formatStockQuantity(getInventoryStockQuantity(item));

const packQuantityFormat = new Intl.NumberFormat('en-IN', {
  maximumFractionDigits: 3,
});

const moneyFormat = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const wholeMoneyFormat = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const getMoneyDisplay = (value) => {
  if (value === undefined) return '-';
  const numericValue = Number(String(value).replace(/,/g, '').trim());
  if (!Number.isFinite(numericValue)) return value || '-';
  return moneyFormat.format(numericValue);
};

const getWholeMoneyDisplay = (value) => {
  if (value === undefined) return '-';
  const numericValue = Number(String(value).replace(/,/g, '').trim());
  if (!Number.isFinite(numericValue)) return value || '-';
  return wholeMoneyFormat.format(Math.ceil(numericValue));
};

const getInventoryPackQuantity = (item) =>
  firstValue(item?.quantity, item?.catalogQuantity, item?.barcode_quantity);

const getInventoryPackQuantityDisplay = (item) => {
  const value = getInventoryPackQuantity(item);
  if (value === undefined) return '-';

  const numericValue = Number(String(value).replace(/,/g, '').trim());
  if (!Number.isFinite(numericValue)) return value || '-';

  return packQuantityFormat.format(numericValue);
};

const getInventoryUnit = (item) =>
  firstValue(item?.unit_short_code, item?.unit_name, item?.units, item?.unit);

const getInventoryUnitMrp = (item) =>
  firstValue(item?.unit_mrp, item?.unit_MRP, item?.inventory_unit_mrp, item?.inventoryUnitMrp);

const getInventoryRawSalesMrp = (item) =>
  firstValue(item?.mrp_amount, item?.mrpAmount, item?.sales_mrp, item?.salesMrp);

const getInventoryPurchasePrice = (item) => {
  if (item?._stockDraft) return Number(item.landed_purchase_unit_price);
  const calculated = calculatePackPricingFromRow(item);
  if (calculated.costPerPiece > 0) return calculated.costPerPiece;

  return Math.ceil(
    Number(
      firstValue(
        item?.landed_purchase_unit_price,
        item?.landedPurchaseUnitPrice,
        item?.purchase_price_with_costs,
        item?.purchasePriceWithCosts,
        item?.unit_price,
        item?.inventory_unit_price,
        item?.purchase_price,
        item?.purchasePrice,
        item?.actual_unit_price,
        item?.expected_unit_price
      ) || 0
    )
  );
};

const getInventorySalesMrp = (item) => {
  const calculated = calculatePackPricingFromRow(item);
  return calculated.mrp || Math.ceil(Number(getInventoryRawSalesMrp(item) || 0));
};

const getInventoryMrp = (item) => {
  if (item?._stockDraft) return Number(item.unit_mrp);
  const isInternalPacking =
    String(item?.business_entity_type || '').trim().toLowerCase() === 'internal_packing';
  const unitMrp = getInventoryUnitMrp(item);
  const salesMrp = getInventorySalesMrp(item);
  const preferredValues = isInternalPacking
    ? [salesMrp, unitMrp]
    : [unitMrp, salesMrp];

  return preferredValues.find((value) => Number(value) > 0) ?? firstValue(...preferredValues);
};

const getInventoryPurchaseStockQuantity = (item) =>
  firstValue(
    item?.count_in_stock,
    item?.countInStock,
    item?.stock,
    item?.no_of_units,
    item?.quantityInStock
  );

const getInventoryPurchaseAmount = (item) => {
  const directAmount = firstValue(
    item?.current_landed_purchase_amount,
    item?.currentLandedPurchaseAmount,
    item?.current_stock_landed_cost,
    item?.currentStockLandedCost
  );

  if (directAmount !== undefined) return directAmount;

  // The batch unit cost is fixed. Dispatching stock reduces only the remaining
  // stock value; it must not divide the original batch amount by fewer units.
  const unitPrice = Number(getInventoryPurchasePrice(item) || 0);
  const stock = Number(getInventoryPurchaseStockQuantity(item) || 0);
  return unitPrice * stock;
};

const columns = [
  {
    key: 'product',
    label: 'Product',
    align: 'left',
    getValue: getInventoryProductDisplayName,
  },
  { key: 'product_telugu', label: 'Telugu name', align: 'left', getValue: getProductTeluguName },
  { key: 'brand', label: 'Brand', align: 'left', getValue: getInventoryBrandName },
  { key: 'category', label: 'Category', align: 'left', getValue: getInventoryCategoryName },
  { key: 'sku', label: 'SKU', align: 'left', getValue: (item) => item.sku_id },
  {
    key: 'barcode',
    label: 'Barcode',
    align: 'left',
    getValue: (item) => item.bar_code,
  },
  { key: 'vendor_barcode', label: 'Vendor Barcode', align: 'left', getValue: item => item.vendor_barcode },
  {
    key: 'quantity',
    label: 'Quantity',
    align: 'right',
    type: 'number',
    getValue: getInventoryPackQuantity,
  },
  {
    key: 'unit',
    label: 'Unit',
    align: 'left',
    getValue: getInventoryUnit,
  },
  {
    key: 'stock',
    label: 'Stock',
    align: 'right',
    type: 'number',
    getValue: getInventoryStockQuantity,
  },
  {
    key: 'purchase_price',
    label: 'Purchase Price + Costs',
    align: 'right',
    type: 'number',
    getValue: getInventoryPurchasePrice,
  },
  {
    key: 'purchase_amount',
    label: 'Purchase Stock Value',
    align: 'right',
    type: 'number',
    getValue: getInventoryPurchaseAmount,
  },
  {
    key: 'mrp_amount',
    label: 'MRP',
    align: 'right',
    type: 'number',
    getValue: getInventoryMrp,
  },
  {
    key: 'warehouse',
    label: 'Warehouse',
    align: 'left',
    getValue: (item) => item.warehouse_id,
  },
  {
    key: 'expiry',
    label: 'Expiry',
    align: 'left',
    type: 'date',
    getValue: (item) => item.exp_date,
  },
];

const SortHeader = ({ column, sortConfig, onSort }) => (
  <th className={`p-3 ${column.align === 'right' ? 'text-right' : 'text-left'}`}>
    <button
      type="button"
      onClick={() => onSort(column.key)}
      className={`inline-flex w-full items-center gap-1 font-semibold hover:text-blue-700 ${
        column.align === 'right' ? 'justify-end' : 'justify-start'
      }`}
    >
      <span>{column.label}</span>
      <span className="text-xs">
        {sortConfig.key === column.key
          ? sortConfig.direction === 'asc'
            ? '^'
            : 'v'
          : '-'}
      </span>
    </button>
  </th>
);

const InventoryProductsTable = ({
  products = [],
  catalogBarcodes = [],
  loading,
  warehouses = [],
  selectedWarehouseId = '',
  onWarehouseChange,
  onEdit,
  onHistory,
  onEditMany,
  onReference,
  onDelete,
  onRollback,
  draftMode = false,
}) => {
  const [search, setSearch] = useState('');
  const [labelProducts, setLabelProducts] = useState(null);
  const [stockLabelProducts, setStockLabelProducts] = useState(null);
  const namedProducts = useMemo(() => {
    const resolveTelugu = createCatalogTeluguResolver(catalogBarcodes);
    const byId = new Map(catalogBarcodes.map(row => [String(row.id || row.product_barcode_id), row]));
    return products.map(item => {
      const catalog = byId.get(String(item.product_barcode_id || item.mkid || '')) ||
        catalogBarcodes.find(row => row.mk_barcode && row.mk_barcode === (item.mk_barcode || item.bar_code));
      return { ...item, product_name_tel: resolveTelugu(item),
        vendor_barcode: item.vendor_barcode || catalog?.barcode ||
          (item.barcode !== (item.mk_barcode || item.bar_code) ? item.barcode : '') || '' };
    });
  }, [products, catalogBarcodes]);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [applySmallerPacks, setApplySmallerPacks] = useState(false);
  const [editForm, setEditForm] = useState({
    stock: '',
    purchasePrice: '',
    mrp: '',
    expiry: '',
  });
  const [exportColumnKeys, setExportColumnKeys] = useState(() => columns
    .filter(column => !['purchase_price', 'purchase_amount', 'mrp_amount'].includes(column.key))
    .map(column => column.key));
  const exportColumns = columns.filter(column => exportColumnKeys.includes(column.key));
  const [sortConfig, setSortConfig] = useState({
    key: 'product',
    direction: 'asc',
  });

  const filteredProducts = useMemo(() => {
    const value = search.toLowerCase();

    return namedProducts.filter((item) => {
      if (Number(getInventoryStockQuantity(item) || 0) < 0) return false;

      return (
        [
          getInventoryProductDisplayName(item),
          getProductTeluguName(item),
          item.product_name,
          item.product_code,
          item.sku_id,
          item.bar_code,
          item.mk_barcode,
          item.vendor_barcode,
          getInventoryPackQuantity(item),
          getInventoryUnit(item),
          getInventoryMrp(item),
          getInventoryBrandName(item),
          getInventoryCategoryName(item),
        ]
          .filter(Boolean)
          .some((field) => String(field).toLowerCase().includes(value))
      );
    });
  }, [namedProducts, search]);

  const sortedProducts = useMemo(
    () => sortRows(filteredProducts, sortConfig, columns),
    [filteredProducts, sortConfig]
  );

  const handleSort = (key) => {
    setSortConfig((current) => getNextSortConfig(current, key));
  };

  const openEdit = (item) => {
    setApplySmallerPacks(false);
    setEditingItem(item);
    setEditForm({
      stock: String(getInventoryStockQuantity(item) ?? ''),
      purchasePrice: String(getInventoryPurchasePrice(item) ?? ''),
      mrp: String(getInventoryMrp(item) ?? ''),
      expiry: item.exp_date ? String(item.exp_date).split('T')[0] : '',
    });
  };

  const updatedPurchaseAmount =
    Number(editForm.stock || 0) * Number(editForm.purchasePrice || 0);
  const smallerPacks = editingItem && onEditMany
    ? smallerPackCosts(editingItem, namedProducts, editForm.purchasePrice) : [];

  const saveEdit = async (event) => {
    event.preventDefault();
    const stock = Number(editForm.stock);
    const purchasePrice = Number(editForm.purchasePrice);
    const mrp = Number(editForm.mrp);
    if (![stock, purchasePrice, mrp].every(Number.isFinite) || stock < 0 || purchasePrice < 0 || mrp < 0) {
      window.alert('Stock, purchase price, and MRP must be valid non-negative numbers.');
      return;
    }

    const previousPurchasePrice = Number(getInventoryPurchasePrice(editingItem) || 0);
    const previousMrp = Number(getInventoryMrp(editingItem) || 0);
    const financialChanged =
      purchasePrice !== previousPurchasePrice || mrp !== previousMrp || (applySmallerPacks && smallerPacks.length > 0);
    let cascadeDispatchFinancials = false;
    let syncPosMongo = false;
    const landedUnits = Number(
      editingItem.landed_purchase_units || editingItem.no_of_units || editingItem.purchase_qty || 0
    );
    const extraCostPerUnit = landedUnits > 0
      ? Number(editingItem.additional_cost_total || 0) / landedUnits
      : 0;
    const basePurchasePrice = Math.max(0, purchasePrice - extraCostPerUnit);

    if (financialChanged) {
      cascadeDispatchFinancials = window.confirm(
        'Purchase price or MRP changed. Update existing dispatch items linked to the inventory batches being saved?'
      );
      syncPosMongo = window.confirm(
        'Also update this product pack in POS (Mongo)? This updates MRP and recalculates the POS discount while preserving the current selling price and stock.'
      );
    }

    setSaving(true);
    try {
      const changes = {
        count_in_stock: stock,
        landed_purchase_unit_price: purchasePrice,
        purchase_price_with_costs: purchasePrice,
        base_purchase_unit_price: basePurchasePrice,
        current_landed_purchase_amount: updatedPurchaseAmount,
        unit_mrp: mrp,
        exp_date: editForm.expiry || null,
        cascade_dispatch_financials: cascadeDispatchFinancials,
        sync_pos_mongo: syncPosMongo,
        financial_changed: financialChanged,
        reason: 'Inventory stock item edited from Stock page',
      };
      const result = applySmallerPacks && smallerPacks.length
        ? await onEditMany([{ id: editingItem.id, changes }, ...smallerPacks.map(({ row, price }) => {
          const units = Number(row.landed_purchase_units || row.no_of_units || row.purchase_qty || 0);
          const extra = units > 0 ? Number(row.additional_cost_total || 0) / units : 0;
          return { id: row.id, changes: {
            count_in_stock: Number(getInventoryStockQuantity(row) || 0),
            landed_purchase_unit_price: price,
            purchase_price_with_costs: price,
            base_purchase_unit_price: Math.max(0, price - extra),
            current_landed_purchase_amount: price * Number(getInventoryStockQuantity(row) || 0),
            unit_mrp: Number(getInventoryMrp(row) || 0),
            exp_date: row.exp_date || null,
            financial_changed: true,
            cascade_dispatch_financials: cascadeDispatchFinancials,
            sync_pos_mongo: syncPosMongo,
            reason: `Purchase cost proportional to inventory #${editingItem.id}`,
          } };
        })]) : await onEdit(editingItem.id, changes);
      setEditingItem(null);
      if (financialChanged && !result?.staged) {
        window.alert(
          `Financial change saved. Dispatch items updated: ${Number(result?.updated_dispatch_items || 0)}. POS financial rows updated: ${Number(result?.updated_pos_financials || 0)}.`
        );
      }
    } catch (error) {
      window.alert(error?.message || error || 'Unable to update stock item');
    } finally {
      setSaving(false);
    }
  };

  const rollbackItem = async (item) => {
    if (!window.confirm(
      `Rollback ${getInventoryProductDisplayName(item)}? This reverses this batch's stock and removes the duplicate item.`
    )) return;
    try {
      await onRollback(item.id, 'Duplicate stock item rolled back from Stock page');
    } catch (error) {
      window.alert(error?.message || error || 'Unable to rollback stock item');
    }
  };

  const deleteItem = async (item) => {
    if (Number(getInventoryStockQuantity(item) || 0) !== 0) {
      window.alert('This item still has stock. Use Rollback to reverse the stock before deleting it.');
      return;
    }
    if (!window.confirm(`Permanently delete ${getInventoryProductDisplayName(item)}?`)) return;
    try {
      await onDelete(item.id, 'Zero-stock inventory item deleted from Stock page');
    } catch (error) {
      window.alert(error?.message || error || 'Unable to delete stock item');
    }
  };

  const dispatchRows = buildStockDispatchRows(sortedProducts, selectedWarehouseId, getInventoryProductDisplayName);
  const marginRows = buildStockMarginRows(sortedProducts, getInventoryProductDisplayName);
  const exportMarginCsv = () => {
    for (let offset = 0; offset < marginRows.length; offset += 1000) {
      const part = marginRows.length > 1000 ? `-part-${Math.floor(offset / 1000) + 1}` : '';
      downloadCsv(`stock-margins-${new Date().toISOString().slice(0, 10)}${part}.csv`, MARGIN_HEADERS, marginRows.slice(offset, offset + 1000));
    }
  };
  const exportDispatchCsv = () => {
    // Match the bulk import limit while retaining every eligible batch.
    for (let offset = 0; offset < dispatchRows.length; offset += 1000) {
      const part = dispatchRows.length > 1000 ? `-part-${Math.floor(offset / 1000) + 1}` : '';
      downloadCsv(`stock-dispatch-${selectedWarehouseId}-${new Date().toISOString().slice(0, 10)}${part}.csv`,
        DISPATCH_STOCK_HEADERS, dispatchRows.slice(offset, offset + 1000));
    }
  };

  const getExportRows = () => sortedProducts.map(item => [
    getInventoryProductDisplayName(item), getProductTeluguName(item) || '-',
    getInventoryBrandName(item) || '-', getInventoryCategoryName(item) || '-', item.sku_id || '-', item.bar_code || '-',
    item.vendor_barcode || '-',
    getInventoryPackQuantityDisplay(item), getInventoryUnit(item) || '-',
    getInventoryStockDisplay(item), getWholeMoneyDisplay(getInventoryPurchasePrice(item)),
    getMoneyDisplay(getInventoryPurchaseAmount(item)), getWholeMoneyDisplay(getInventoryMrp(item)),
    item.warehouse_id || '-', item.exp_date ? String(item.exp_date).split('T')[0] : '-',
  ].filter((value, index) => exportColumnKeys.includes(columns[index].key)));

  const exportCsv = () => {
    const escape = value => {
      let text = String(value ?? '');
      if (/^[=+@\-\t\r]/.test(text)) text = `'${text}`;
      return `"${text.replace(/"/g, '""')}"`;
    };
    const csv = [exportColumns.map(column => column.label), ...getExportRows()]
      .map(row => row.map(escape).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `inventory-stock-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    const preview = window.open('', '_blank');
    if (!preview) {
      window.alert('Please allow pop-ups to open the PDF export.');
      return;
    }
    preview.opener = null;
    const escape = value => String(value ?? '').replace(/[&<>"']/g, char => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[char]));
    const warehouse = warehouses.find(row => String(row.id) === String(selectedWarehouseId));
    const warehouseLabel = selectedWarehouseId
      ? warehouse?.warehouse_name || warehouse?.warehouse_code || selectedWarehouseId : 'All inventory';
    const exportedAt = new Date();
    const dateLabel = exportedAt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    const timeLabel = exportedAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' });
    const columnWidths = { product: 48, product_telugu: 48, brand: 28, category: 28, sku: 65, barcode: 30, vendor_barcode: 30, quantity: 18, unit: 16, stock: 18,
      purchase_price: 26, purchase_amount: 28, mrp_amount: 22, warehouse: 22, expiry: 24 };
    const preferredWidth = exportColumns.reduce((total, column) => total + columnWidths[column.key], 0);
    const orientation = preferredWidth <= 190 ? 'portrait' : 'landscape';
    const tableWidth = Math.min(preferredWidth, orientation === 'portrait' ? 190 : 277);
    preview.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Manakirana Stock Sheet</title>
      <style>@page{size:A4 ${orientation};margin:10mm}body{font-family:Arial,sans-serif;color:#111;font-size:10px;margin:0}h1{font-size:22px;margin:0 0 8px;text-align:center}table{width:${tableWidth}mm;max-width:100%;margin:0 auto;border-collapse:collapse;table-layout:fixed}th,td{border:1px solid #ccc;padding:6px;overflow-wrap:anywhere;text-align:left}th{background:#eee}thead{display:table-header-group}tr{break-inside:avoid}.sheet-header{background:white;border:0;padding:0 0 12px}.sheet-meta{display:flex;flex-wrap:wrap;justify-content:space-between;gap:4px 12px;font-weight:normal;line-height:1.6}.sheet-search{margin-top:4px;font-weight:normal}@media screen{body{max-width:${orientation === 'portrait' ? 190 : 277}mm;margin:16px auto;padding:10mm;background:white}}@media print{button,.hint{display:none}}</style>
      </head><body><button onclick="window.print()">Print / Save as PDF</button><p class="hint">A4 ${orientation}. Choose Save as PDF in the print dialog. Turn off browser Headers and footers for a clean stock sheet.</p>
      <table><colgroup>${exportColumns.map(column => `<col style="width:${columnWidths[column.key] / preferredWidth * 100}%">`).join('')}</colgroup><thead>
      <tr><th colspan="${exportColumns.length}" class="sheet-header"><h1>Manakirana Stock Sheet</h1>
      <div class="sheet-meta"><span>Warehouse: ${escape(warehouseLabel)}</span><span>Date: ${escape(dateLabel)} &nbsp; Time: ${escape(timeLabel)}</span><span>Total Rows: ${sortedProducts.length}</span></div>
      ${search ? `<div class="sheet-search">Search: ${escape(search)}</div>` : ''}</th></tr>
      <tr>${exportColumns.map(column => `<th>${escape(column.label)}</th>`).join('')}</tr></thead>
      <tbody>${getExportRows().map(row => `<tr>${row.map(value => `<td>${escape(value)}</td>`).join('')}</tr>`).join('')}</tbody></table></body></html>`);
    preview.document.close();
    preview.onload = () => { preview.focus(); preview.print(); };
  };

  return (
    <section className="bg-white rounded-xl border shadow-sm p-4">
      {stockLabelProducts && <InventoryStockLabelPrint products={stockLabelProducts} catalogBarcodes={catalogBarcodes} onClose={() => setStockLabelProducts(null)} />}
      {labelProducts && <InventoryLabelExport products={labelProducts} catalogBarcodes={catalogBarcodes} onClose={() => setLabelProducts(null)} />}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <h2 className="font-bold text-lg">Inventory Products</h2>

        <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row md:flex-wrap">
          <button type="button" onClick={exportCsv} disabled={loading || !sortedProducts.length || !exportColumns.length}
            className="rounded-lg border px-3 py-2 text-sm font-semibold disabled:opacity-50">Export CSV</button>
          <button type="button" onClick={exportPdf} disabled={loading || !sortedProducts.length || !exportColumns.length}
            className="rounded-lg border px-3 py-2 text-sm font-semibold disabled:opacity-50">Export PDF</button>
          <button type="button" onClick={() => setLabelProducts(sortedProducts)} disabled={loading || !sortedProducts.length}
            className="rounded-lg border px-3 py-2 text-sm font-semibold disabled:opacity-50">Export Labels</button>
          <button type="button" onClick={exportDispatchCsv} disabled={loading || !selectedWarehouseId || !dispatchRows.length}
            className="rounded-lg border border-blue-300 px-3 py-2 text-sm font-semibold text-blue-700 disabled:opacity-50">Export Dispatch CSV</button>
          <button type="button" onClick={exportMarginCsv} disabled={loading || !marginRows.length}
            className="rounded-lg border border-emerald-300 px-3 py-2 text-sm font-semibold text-emerald-700 disabled:opacity-50">Export Margin CSV</button>
          {onWarehouseChange ? (
            <select
              value={selectedWarehouseId}
              onChange={(e) => onWarehouseChange(e.target.value)}
              className="w-full rounded-lg border px-3 py-2 md:w-56"
            >
              <option value="">All inventory</option>
              {warehouses.map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.warehouse_name || warehouse.warehouse_code || warehouse.id}
                </option>
              ))}
            </select>
          ) : null}

          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search product / brand / category / SKU / barcode / vendor barcode"
            className="border rounded-lg px-3 py-2 w-full md:w-80"
          />
        </div>
      </div>

      <p className="mb-3 text-xs text-gray-600">For bulk dispatch, select a warehouse and export Dispatch CSV. Available units are prefilled; edit no_of_units and remove unwanted rows. In Create Dispatch, select the same source warehouse and an outlet or warehouse destination, upload the CSV, and review before creating the dispatch. Export uses the current search and a fixed dispatch format; files contain up to 1,000 rows each.</p>
      {selectedWarehouseId && sortedProducts.length > dispatchRows.length && <p className="mb-3 text-xs text-amber-700">{sortedProducts.length - dispatchRows.length} rows excluded from dispatch export because they are inactive or lack a valid batch ID, barcode, expiry, or available units.</p>}
      <p className="mb-3 text-xs text-gray-600">For bulk margin configuration, export Margin CSV, fill margin_percent, then upload CSV or Excel in Cost &amp; Ledger Config → Product Pack Margins. The export follows the current warehouse and search filters and combines batches of the same product pack within each warehouse.</p>
      <details className="mb-4 rounded-lg border p-3">
        <summary className="cursor-pointer text-sm font-semibold">Export columns ({exportColumns.length} selected)</summary>
        <p className="mt-2 text-xs text-gray-500">Choose columns for CSV and PDF. Financial columns are excluded by default.</p>
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2">
          {columns.map(column => (
            <label key={column.key} className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={exportColumnKeys.includes(column.key)}
                onChange={event => setExportColumnKeys(current => event.target.checked
                  ? [...current, column.key] : current.filter(key => key !== column.key))} />
              {column.label}
            </label>
          ))}
        </div>
        {!exportColumns.length && <p className="mt-2 text-sm text-red-600">Select at least one column to export.</p>}
      </details>

      {loading ? (
        <p className="text-gray-500">Loading inventory...</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                {columns.map((column) => (
                  <SortHeader
                    key={column.key}
                    column={column}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                ))}
                <th className="p-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>

            <tbody>
              {sortedProducts.map((item) => (
                <tr key={item.id} onClick={onHistory ? () => onHistory(item) : undefined} className={`border-b hover:bg-gray-50 ${onHistory ? 'cursor-pointer' : ''}`}>
                  <td className="p-3 font-medium">{getInventoryProductDisplayName(item)}{item._stockDraft && <span className="ml-2 text-xs text-amber-700">Unpublished</span>}</td>
                  <td className="p-3" lang="te">{getProductTeluguName(item) || '-'}</td>
                  <td className="p-3">{getInventoryBrandName(item) || '-'}</td>
                  <td className="p-3">{getInventoryCategoryName(item) || '-'}</td>
                  <td className="p-3">{item.sku_id}</td>
                  <td className="p-3">{item.bar_code || '-'}</td>
                  <td className="p-3">{item.vendor_barcode || '-'}</td>
                  <td className="p-3 text-right">{getInventoryPackQuantityDisplay(item)}</td>
                  <td className="p-3">{getInventoryUnit(item) || '-'}</td>
                  <td className="p-3 text-right font-semibold">
                    {getInventoryStockDisplay(item)}
                  </td>
                  <td className="p-3 text-right">{getWholeMoneyDisplay(getInventoryPurchasePrice(item))}</td>
                  <td className="p-3 text-right">{getMoneyDisplay(getInventoryPurchaseAmount(item))}</td>
                  <td className="p-3 text-right">{getWholeMoneyDisplay(getInventoryMrp(item))}</td>
                  <td className="p-3">{item.warehouse_id || '-'}</td>
                  <td className="p-3">
                    {item.exp_date
  ? String(item.exp_date).split('T')[0]
  : '-'}
                  </td>
                    <td className="p-3">
                      <div onClick={e => e.stopPropagation()} className="flex min-w-max justify-end gap-2">
                        <button type="button" onClick={() => setStockLabelProducts([item])}
                          className="rounded border border-blue-300 px-2 py-1 font-semibold text-blue-700 hover:bg-blue-50">
                          Label Print
                        </button>
                        {onEdit && (
                          <button type="button" onClick={() => openEdit(item)}
                            className="rounded border border-blue-300 px-2 py-1 font-semibold text-blue-700 hover:bg-blue-50">
                            Edit
                          </button>
                        )}
                        {onReference && <label className="flex items-center gap-2"><input type="checkbox" checked={!!item.is_pricing_reference} disabled={loading} onChange={e=>onReference(item.id,e.target.checked)}/>Pricing reference</label>}
                        {onRollback && (
                          <button type="button" onClick={() => rollbackItem(item)}
                            className="rounded border border-amber-300 px-2 py-1 font-semibold text-amber-700 hover:bg-amber-50">
                            Rollback
                          </button>
                        )}
                        {onDelete && (
                          <button type="button" onClick={() => deleteItem(item)}
                            className="rounded border border-red-300 px-2 py-1 font-semibold text-red-700 hover:bg-red-50">
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                </tr>
              ))}

              {sortedProducts.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 1} className="p-4 text-center text-gray-500">
                    No inventory products found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editingItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
          <form onSubmit={saveEdit} className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-bold">Edit Stock Item</h3>
            <p className="mt-1 text-sm text-gray-500">{getInventoryProductDisplayName(editingItem)}</p>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="text-sm font-semibold">Stock
                <input type="number" min="0" step="any" required value={editForm.stock}
                  onChange={event => setEditForm(current => ({ ...current, stock: event.target.value }))}
                  className="mt-1 w-full rounded-lg border px-3 py-2 font-normal" />
              </label>
              <label className="text-sm font-semibold">Purchase Price + Costs
                <input type="number" min="0" step="any" required value={editForm.purchasePrice}
                  onChange={event => setEditForm(current => ({ ...current, purchasePrice: event.target.value }))}
                  className="mt-1 w-full rounded-lg border px-3 py-2 font-normal" />
              </label>
              <label className="text-sm font-semibold">MRP
                <input type="number" min="0" step="any" required value={editForm.mrp}
                  onChange={event => setEditForm(current => ({ ...current, mrp: event.target.value }))}
                  className="mt-1 w-full rounded-lg border px-3 py-2 font-normal" />
              </label>
              <label className="text-sm font-semibold">Expiry
                <input type="date" value={editForm.expiry}
                  onChange={event => setEditForm(current => ({ ...current, expiry: event.target.value }))}
                  className="mt-1 w-full rounded-lg border px-3 py-2 font-normal" />
              </label>
            </div>
            <div className="mt-4 rounded-lg bg-blue-50 p-3 text-sm text-blue-900">
              Updated purchase stock value: <strong>{getMoneyDisplay(updatedPurchaseAmount)}</strong>
              <div className="mt-1 text-xs">Calculated automatically as stock × purchase price.</div>
            </div>
            {smallerPacks.length > 0 && (
              <div className="mt-4 rounded-lg border p-3 text-sm">
                <label className="flex items-center gap-2 font-semibold">
                  <input type="checkbox" checked={applySmallerPacks}
                    onChange={event => setApplySmallerPacks(event.target.checked)} />
                  Apply proportional cost to smaller packs
                </label>
                <p className="mt-2 text-gray-600">Same product, brand, category and warehouse. Purchase price includes costs and rounds up to whole rupees. MRP stays unchanged.</p>
                {applySmallerPacks && <ul className="mt-2 space-y-1">
                  {smallerPacks.map(({ row, price }) => <li key={row.id}>
                    {getInventoryPackQuantity(row)} {getInventoryUnit(row)} · #{row.id}: {getMoneyDisplay(getInventoryPurchasePrice(row))} → ₹{getMoneyDisplay(price)}
                  </li>)}
                </ul>}
              </div>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setEditingItem(null)} disabled={saving}
                className="rounded-lg border px-4 py-2 font-semibold">Cancel</button>
              <button type="submit" disabled={saving}
                className="rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white disabled:opacity-50">
                {saving ? 'Saving...' : draftMode ? 'Save draft' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
};

export default InventoryProductsTable;
